import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '@/api/services/users.api';
import { queryKeys } from '@/api/queryKeys';
import { BulkWorkOrderEditDialog } from '@/components/work-orders/BulkWorkOrderEditDialog';
import type { BulkResult } from '@/components/common/BulkFieldEditDialog';
import { format, isPast } from 'date-fns';
import {
  ClipboardList,
  Plus,
  Clock,
  AlertTriangle,
  CheckCircle,
  Wrench,
  User,
  MapPin,
  MoreVertical,
  Play,
  Pause,
  XCircle,
  Pencil,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  PageHeader,
  StatusBadge,
  MetricCard,
  KPIGrid,
  DataTable,
  Column,
  TableFilters,
} from '@/components/common';
import {
  WorkOrder,
  CreateWorkOrderDto,
  WORK_ORDER_TYPES,
  WORK_ORDER_PRIORITIES,
  WORK_ORDER_STATUSES,
} from '@/api/services/work-orders.api';
import {
  useWorkOrdersList,
  useWorkOrdersDashboard,
  useCreateWorkOrder,
  useUpdateWorkOrderStatus,
} from '@/api/hooks';
import { useEquipmentList } from '@/api/hooks';
import { useLocationsList } from '@/api/hooks';

export default function WorkOrders() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');

  // Selected ids survive a filter change but their rows do not, which would
  // leave the bar offering to edit orders that are no longer on screen.
  useEffect(() => {
    setSelectedIds([]);
  }, [statusFilter, priorityFilter, typeFilter]);

  // Assignees for the bulk dialog. Scoped to the caller's company by the API.
  const { data: usersData } = useQuery({
    queryKey: ['users', 'assignees'],
    queryFn: () => usersApi.getAll({ limit: 200 }),
  });
  const assignees = usersData?.data ?? [];
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [page, setPage] = useState(1);

  // Form state
  const [formData, setFormData] = useState({
    type: 'CORRECTIVE',
    priority: 'MEDIUM',
    title: '',
    description: '',
    equipmentId: '',
    locationId: '',
    scheduledStart: '',
    scheduledEnd: '',
    estimatedHours: '',
  });

  // React Query hooks for data fetching
  const { data: workOrdersData, isLoading: workOrdersLoading } = useWorkOrdersList({
    page,
    limit: 20,
    ...(search && { search }),
    ...(statusFilter && { status: statusFilter }),
    ...(priorityFilter && { priority: priorityFilter }),
    ...(typeFilter && { type: typeFilter }),
  });

  const { data: dashboardData } = useWorkOrdersDashboard();
  const { data: equipmentData } = useEquipmentList({ limit: 100 });
  const { data: locationsData } = useLocationsList({ limit: 100 });

  // Mutations
  const createMutation = useCreateWorkOrder();
  const updateStatusMutation = useUpdateWorkOrderStatus();

  // Derived data
  const workOrders = workOrdersData?.data?.data ?? [];
  const totalPages = workOrdersData?.data?.pagination?.totalPages ?? 1;
  const dashboard = dashboardData?.data ?? null;
  const equipment = equipmentData?.data?.data ?? [];
  const locations = locationsData?.data ?? [];
  const loading = workOrdersLoading;

  const handleCreate = async () => {
    const data: CreateWorkOrderDto = {
      type: formData.type,
      priority: formData.priority,
      title: formData.title,
      locationId: formData.locationId,
      ...(formData.description && { description: formData.description }),
      ...(formData.equipmentId && { equipmentId: formData.equipmentId }),
      ...(formData.scheduledStart && { scheduledStart: formData.scheduledStart }),
      ...(formData.scheduledEnd && { scheduledEnd: formData.scheduledEnd }),
      ...(formData.estimatedHours && { estimatedHours: parseFloat(formData.estimatedHours) }),
    };

    createMutation.mutate(data, {
      onSuccess: (result) => {
        setIsCreateOpen(false);
        resetForm();
        navigate(`/work-orders/${result.data.id}`);
      },
    });
  };

  const handleStatusUpdate = (wo: WorkOrder, newStatus: string) => {
    updateStatusMutation.mutate({ id: wo.id, data: { status: newStatus } });
  };

  const resetForm = () => {
    setFormData({
      type: 'CORRECTIVE',
      priority: 'MEDIUM',
      title: '',
      description: '',
      equipmentId: '',
      locationId: '',
      scheduledStart: '',
      scheduledEnd: '',
      estimatedHours: '',
    });
  };

  const getOpenCount = () => {
    return (
      (dashboard?.byStatus?.DRAFT || 0) +
      (dashboard?.byStatus?.PENDING_APPROVAL || 0) +
      (dashboard?.byStatus?.APPROVED || 0) +
      (dashboard?.byStatus?.SCHEDULED || 0) +
      (dashboard?.byStatus?.IN_PROGRESS || 0) +
      (dashboard?.byStatus?.ON_HOLD || 0)
    );
  };

  const workOrderColumns: Column<WorkOrder>[] = [
    {
      key: 'workOrderNumber',
      linkTo: (wo) => `/work-orders/${wo.id}`,
      header: 'Work Order',
      sortable: true,
      className: 'min-w-[250px]',
      // Titles run long; left to wrap they pushed rows from 65px to 105px and
      // the table lost its rhythm. Truncated, with the full text on hover.
      render: (wo) => (
        <div className="max-w-[260px]">
          <div className="font-medium">{wo.workOrderNumber}</div>
          <div className="truncate text-sm text-muted-foreground" title={wo.title}>
            {wo.title}
          </div>
        </div>
      ),
    },
    {
      key: 'equipment',
      header: 'Equipment',
      className: 'min-w-[220px]',
      render: (wo) =>
        wo.equipment ? (
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="text-sm">{wo.equipment.name}</div>
              <div className="text-xs text-muted-foreground">{wo.equipment.assetNumber}</div>
            </div>
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (wo) => (
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm">{wo.location?.name}</span>
        </div>
      ),
    },
    {
      key: 'assignedTo',
      header: 'Assignee',
      render: (wo) =>
        wo.assignedTo ? (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{wo.assignedTo.fullName}</span>
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">Unassigned</span>
        ),
    },
    {
      key: 'scheduledStart',
      header: 'Scheduled',
      sortable: true,
      render: (wo) =>
        wo.scheduledStart ? (
          <div className="space-y-1">
            <div className="text-sm">{format(new Date(wo.scheduledStart), 'MMM d, yyyy')}</div>
            {wo.scheduledEnd &&
              isPast(new Date(wo.scheduledEnd)) &&
              !['COMPLETED', 'CANCELLED', 'CLOSED'].includes(wo.status) && (
                <Badge variant="outline" className="text-danger-500 border-danger-200">
                  Overdue
                </Badge>
              )}
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">Not scheduled</span>
        ),
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      render: (wo) => <StatusBadge status={wo.priority} />,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (wo) => (
        <StatusBadge
          status={wo.status}
          label={WORK_ORDER_STATUSES.find((s) => s.value === wo.status)?.label}
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-[80px]',
      render: (wo) => (
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {wo.status === 'DRAFT' && (
                <DropdownMenuItem onClick={() => handleStatusUpdate(wo, 'PENDING_APPROVAL')}>
                  Submit for Approval
                </DropdownMenuItem>
              )}
              {wo.status === 'PENDING_APPROVAL' && (
                <DropdownMenuItem onClick={() => handleStatusUpdate(wo, 'APPROVED')}>
                  Approve
                </DropdownMenuItem>
              )}
              {wo.status === 'APPROVED' && (
                <>
                  <DropdownMenuItem onClick={() => handleStatusUpdate(wo, 'SCHEDULED')}>
                    Schedule
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusUpdate(wo, 'IN_PROGRESS')}>
                    <Play className="mr-2 h-4 w-4" />
                    Start Work
                  </DropdownMenuItem>
                </>
              )}
              {wo.status === 'SCHEDULED' && (
                <DropdownMenuItem onClick={() => handleStatusUpdate(wo, 'IN_PROGRESS')}>
                  <Play className="mr-2 h-4 w-4" />
                  Start Work
                </DropdownMenuItem>
              )}
              {wo.status === 'IN_PROGRESS' && (
                <>
                  <DropdownMenuItem onClick={() => handleStatusUpdate(wo, 'ON_HOLD')}>
                    <Pause className="mr-2 h-4 w-4" />
                    Put On Hold
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(`/work-orders/${wo.id}`)}>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Complete
                  </DropdownMenuItem>
                </>
              )}
              {wo.status === 'ON_HOLD' && (
                <DropdownMenuItem onClick={() => handleStatusUpdate(wo, 'IN_PROGRESS')}>
                  <Play className="mr-2 h-4 w-4" />
                  Resume
                </DropdownMenuItem>
              )}
              {wo.status === 'COMPLETED' && (
                <DropdownMenuItem onClick={() => handleStatusUpdate(wo, 'CLOSED')}>
                  Close Work Order
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {!['COMPLETED', 'CANCELLED', 'CLOSED'].includes(wo.status) && (
                <DropdownMenuItem
                  onClick={() => handleStatusUpdate(wo, 'CANCELLED')}
                  className="text-danger-600"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Cancel
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      {/* Header */}
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Work Orders' }]}
          title="Work Orders"
          description="Maintenance and repair tasks raised against assets, with assignee and scheduled date."
          actions={
            <Button onClick={() => navigate('/work-orders/new')}>
              <Plus className="mr-2 h-4 w-4" />
              New Work Order
            </Button>
          }
        />
      </motion.div>

      {/* Dashboard KPI Cards */}
      {dashboard && (
        <motion.div variants={fadeUp} className="mt-6">
          <KPIGrid columns={4}>
            <MetricCard
              label="Open Work Orders"
              value={getOpenCount()}
              sentiment="info"
              icon={<ClipboardList className="h-5 w-5" />}
            />
            <MetricCard
              label="Overdue"
              value={dashboard.overdue}
              sentiment={dashboard.overdue > 0 ? 'danger' : 'success'}
              icon={<AlertTriangle className="h-5 w-5" />}
            />
            <MetricCard
              label="Completed This Month"
              value={dashboard.completedThisMonth}
              sentiment="success"
              icon={<CheckCircle className="h-5 w-5" />}
            />
            <MetricCard
              label="Avg. Completion Time"
              value={dashboard.avgCompletionTime ? `${dashboard.avgCompletionTime}h` : '-'}
              sentiment="neutral"
              icon={<Clock className="h-5 w-5" />}
            />
          </KPIGrid>
        </motion.div>
      )}

      {/* Work Orders Table */}
      <motion.div variants={fadeUp} className="mt-6">
        <DataTable<WorkOrder>
          toolbar={
            <TableFilters
              search={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search work orders..."
              filters={[
                {
                  id: 'status',
                  label: 'Statuses',
                  value: statusFilter,
                  options: WORK_ORDER_STATUSES,
                  onChange: setStatusFilter,
                  placeholder: 'Status',
                },
                {
                  id: 'priority',
                  label: 'Priorities',
                  value: priorityFilter,
                  options: WORK_ORDER_PRIORITIES,
                  onChange: setPriorityFilter,
                  placeholder: 'Priority',
                },
                {
                  id: 'type',
                  label: 'Types',
                  value: typeFilter,
                  options: WORK_ORDER_TYPES,
                  onChange: setTypeFilter,
                  placeholder: 'Type',
                },
              ]}
            />
          }
          data={workOrders}
          columns={workOrderColumns}
          loading={loading}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          getRowId={(wo) => wo.id}
          onRowClick={(wo) => navigate(`/work-orders/${wo.id}`)}
          emptyTitle="No work orders found"
        />
      </motion.div>

      {selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-xl border bg-card p-3 shadow-floating">
          <span className="pl-1 text-sm font-medium">
            {selectedIds.length} work order{selectedIds.length > 1 ? 's' : ''} selected
          </span>
          <Button size="sm" onClick={() => setBulkEditOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit fields
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
            <X className="mr-2 h-4 w-4" />
            Clear
          </Button>
        </div>
      )}

      <BulkWorkOrderEditDialog
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        workOrderIds={selectedIds}
        assignees={assignees}
        onApplied={(result: BulkResult) => {
          queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
          // Keep the failures selected so a retry acts on exactly those — the
          // common case here is a status the order could not transition to.
          setSelectedIds(result.results.filter((r) => r.status === 'failed').map((r) => r.id));
        }}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* Create Work Order Sheet */}
      <Sheet open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Create Work Order</SheetTitle>
            <SheetDescription>Create a new work order for maintenance or repair</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="e.g., Replace hydraulic pump"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(v) => setFormData({ ...formData, type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WORK_ORDER_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(v) => setFormData({ ...formData, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WORK_ORDER_PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Select
                value={formData.locationId}
                onValueChange={(v) => setFormData({ ...formData, locationId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc: any) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="equipment">Equipment (Optional)</Label>
              <Select
                value={formData.equipmentId}
                onValueChange={(v) => setFormData({ ...formData, equipmentId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select equipment" />
                </SelectTrigger>
                <SelectContent>
                  {equipment.map((eq) => (
                    <SelectItem key={eq.id} value={eq.id}>
                      {eq.name} ({eq.assetNumber})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="scheduledStart">Scheduled Start</Label>
                <Input
                  id="scheduledStart"
                  type="datetime-local"
                  value={formData.scheduledStart}
                  onChange={(e) => setFormData({ ...formData, scheduledStart: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="scheduledEnd">Scheduled End</Label>
                <Input
                  id="scheduledEnd"
                  type="datetime-local"
                  value={formData.scheduledEnd}
                  onChange={(e) => setFormData({ ...formData, scheduledEnd: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="estimatedHours">Estimated Hours</Label>
              <Input
                id="estimatedHours"
                type="number"
                step="0.5"
                placeholder="4.0"
                value={formData.estimatedHours}
                onChange={(e) => setFormData({ ...formData, estimatedHours: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Detailed description of the work required..."
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!formData.title || !formData.locationId || createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create Work Order'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </motion.main>
  );
}
